import { forwardRef, useEffect, useRef, useImperativeHandle, useState, useCallback, memo } from "react";
import { cn } from "@/lib/utils";
import { useTeleprompterLines } from "@/hooks/useTeleprompterLines";

interface TeleprompterDisplayProps {
  content: string;
  fontSize: number;
  fontColor: string;
  bgColor: string;
  mirrorMode: boolean;
  scrollPosition: number;
  lineHeight?: number; // 1.0 - 3.0
  horizontalPadding?: number; // 5 - 40% (percentage of screen width)
  onMouseMove: () => void;
  onClick?: () => void; // For tap-to-dismiss controls on mobile
  onSwipeUp?: () => void; // For swipe-up-to-show controls on mobile
  onScrollUpdate?: (newPosition: number) => void;
  onManualScrollDelta?: (delta: number) => void;
  isPlaying?: boolean;
}

export const TeleprompterDisplay = memo(forwardRef<HTMLDivElement, TeleprompterDisplayProps>(
  ({ content, fontSize, fontColor, bgColor, mirrorMode, scrollPosition, lineHeight = 1.5, horizontalPadding = 10, onMouseMove, onClick, onSwipeUp, onScrollUpdate, onManualScrollDelta, isPlaying = false }, ref) => {
    const innerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    // Removed currentLineIndex state for smooth animation
    const rafRef = useRef<number | null>(null);
    const isUserScrolling = useRef(false);
    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastTouchYRef = useRef<number | null>(null);
    
    // State to track container width for visual line calculations
    const [containerWidth, setContainerWidth] = useState(0);

    // Expose inner ref to parent
    useImperativeHandle(ref, () => innerRef.current as HTMLDivElement);
    
    // Measure container width on mount and resize
    useEffect(() => {
      if (!innerRef.current) return;
      
      const updateWidth = () => {
        if (innerRef.current) {
          setContainerWidth(innerRef.current.clientWidth);
        }
      };
      
      updateWidth();
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }, []);

    // Use our new hook to split content into visual lines
    // Convert horizontalPadding percentage to pixels approx for calculation
    const paddingPx = containerWidth * (horizontalPadding / 100);
    
    const lines = useTeleprompterLines({
      content,
      fontSize,
      containerWidth,
      paddingLeft: paddingPx,
      paddingRight: paddingPx,
    });
    
    const containerHeight = typeof window !== "undefined" ? window.innerHeight : 800;
    
    // Calculate estimated line height in pixels for layout
    const lineHeightPx = fontSize * lineHeight;
    
    // Center offset - the first line should start in the middle of the screen
    const centerOffset = containerHeight / 2 - (lineHeightPx / 2);
    
    const isProgrammaticScroll = useRef(false);
    
    // Apply scroll position from prop when not user scrolling
    // Apply scroll position from prop when not user scrolling AND not playing (to avoid fighting the animation loop)
    useEffect(() => {
      // Check if we need to update to avoid setting flag unnecessarily
      if (innerRef.current && !isUserScrolling.current && !isPlaying) {
        if (Math.abs(innerRef.current.scrollTop - scrollPosition) > 1) {
          isProgrammaticScroll.current = true;
          innerRef.current.scrollTop = scrollPosition;
          // Reset flag after a short delay to allow scroll event to fire and be ignored
          setTimeout(() => {
             isProgrammaticScroll.current = false;
          }, 100);
        }
      }
    }, [scrollPosition, isPlaying]);
    
    // Hybrid Scrolling Logic
    // Exposed to parent via DOM property
    useEffect(() => {
        if (innerRef.current) {
            interface TeleprompterContainer extends HTMLDivElement {
              setSmoothScroll?: (y: number, isPlaying: boolean) => void;
              updateHighlight?: () => void;
            }
            const container = innerRef.current as TeleprompterContainer;
            
            container.setSmoothScroll = (y: number, isPlaying: boolean) => {
                if (!innerRef.current || !contentRef.current) return;

                if (isPlaying) {
                    // TRANSFORM MODE (Sub-pixel smooth)
                    // Only apply layout constraints if not already applied (prevents thrashing)
                    if (innerRef.current.style.overflowY !== 'hidden') {
                        innerRef.current.style.overflowY = 'hidden';
                        innerRef.current.scrollTop = 0; 
                    } else if (innerRef.current.scrollTop !== 0) {
                        // Ensure it stays at 0 (resilience)
                        innerRef.current.scrollTop = 0;
                    }
                    
                    // Use translate3d for GPU acceleration
                    contentRef.current.style.transform = `translate3d(0, -${y}px, 0)`;
                } else {
                    // NATIVE MODE (Interaction ready)
                    innerRef.current.style.overflowY = 'auto';
                    contentRef.current.style.transform = 'none';
                    innerRef.current.scrollTop = y;
                }
            };
            
            // Backward compatibility stub (function now does nothing, highlighting is CSS-driven)
            container.updateHighlight = () => {};
        }
    }, []);


    useEffect(() => {
      const container = innerRef.current;
      if (!container) return;
      
      const handleScroll = () => {
        if (isProgrammaticScroll.current) return;
        
        if (onScrollUpdate) {
           onScrollUpdate(container.scrollTop);
        }
      };
      
      const handleUserInteraction = () => {
        isUserScrolling.current = true;
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
        scrollTimeoutRef.current = setTimeout(() => {
          isUserScrolling.current = false;
        }, 100);
      };

      const handleWheel = (e: WheelEvent) => {
        if (isPlaying && onManualScrollDelta) {
          // Send delta to hook to override playback position
          onManualScrollDelta(e.deltaY);
        } else {
          handleUserInteraction();
        }
      };

      const handleTouchMove = (e: TouchEvent) => {
        if (isPlaying && onManualScrollDelta && e.touches.length === 1) {
          const currentY = e.touches[0].clientY;
          if (lastTouchYRef.current !== null) {
            const deltaY = lastTouchYRef.current - currentY;
            onManualScrollDelta(deltaY);
          }
          lastTouchYRef.current = currentY;
        } else {
          handleUserInteraction();
        }
      };

      const handleTouchStart = (e: TouchEvent) => {
        if (e.touches.length === 1) {
          lastTouchYRef.current = e.touches[0].clientY;
        }
      };

      const handleTouchEnd = () => {
        lastTouchYRef.current = null;
      };
      
      container.addEventListener('scroll', handleScroll);
      container.addEventListener('wheel', handleWheel, { passive: true });
      container.addEventListener('touchmove', handleTouchMove, { passive: true });
      container.addEventListener('touchstart', handleTouchStart, { passive: true });
      container.addEventListener('touchend', handleTouchEnd, { passive: true });
      
      return () => {
        container.removeEventListener('scroll', handleScroll);
        container.removeEventListener('wheel', handleWheel);
        container.removeEventListener('touchmove', handleTouchMove);
        container.removeEventListener('touchstart', handleTouchStart);
        container.removeEventListener('touchend', handleTouchEnd);
        if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      };
    }, [onScrollUpdate, onManualScrollDelta, isPlaying]);

    // Custom tap detection logic
    const touchStartRef = useRef<{x: number, y: number} | null>(null);

    const handleTouchStart = (e: React.TouchEvent) => {
      // Don't stop propagation - let parent handle wake lock etc if needed
      if (e.touches.length === 1) {
        touchStartRef.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY
        };
      }
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
      if (!touchStartRef.current) return;
      
      const touch = e.changedTouches[0];
      const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
      const deltaY = touchStartRef.current.y - touch.clientY; // positive = swipe up (finger moves up)
      const absDeltaY = Math.abs(deltaY);
      
      const TAP_THRESHOLD = 30; // 放寬到 30px
      const SWIPE_THRESHOLD = 50;
      const BOTTOM_EDGE_ZONE = 0.3; // Increased to 30% to be safer
      
      console.log('[TeleprompterDisplay] Touch end - deltaX:', deltaX, 'deltaY:', Math.abs(touch.clientY - touchStartRef.current.y));
      
      // Tap detection (small movement)
      if (deltaX < TAP_THRESHOLD && Math.abs(touch.clientY - touchStartRef.current.y) < TAP_THRESHOLD) {
        console.log('[TeleprompterDisplay] Tap detected - calling onClick');
        onClick?.();
      }
      // Swipe Up detection (from bottom area)
      else {
        const screenHeight = window.innerHeight;
        const startedFromBottomEdge = touchStartRef.current.y > screenHeight * (1 - BOTTOM_EDGE_ZONE);
        
        if (startedFromBottomEdge && deltaY > SWIPE_THRESHOLD) {
             console.log('[TeleprompterDisplay] Swipe up detected - calling onSwipeUp');
             onSwipeUp?.();
        } else {
          console.log('[TeleprompterDisplay] Not a tap or swipe - ignored');
        }
      }
      
      touchStartRef.current = null;
    };

    return (
      <div
        className={cn(
          "h-screen w-full overflow-hidden select-none",
          mirrorMode && "scale-x-[-1]"
        )}
        style={{ backgroundColor: bgColor }}
        onMouseMove={onMouseMove}
        // Remove onClick from outer div
          // Removing maskImage as it causes GPU compositing stutter on some mobile devices
          // when combined with NoSleep video loop.
        >
          {/* Top Gradient Overlay - Simulates mask using alpha blending (Cheaper) */}
          <div 
            className="absolute top-0 left-0 w-full z-10 pointer-events-none"
            style={{
              height: '45vh', // Cover top 45%
              background: `linear-gradient(to bottom, ${bgColor} 10%, transparent 100%)`
            }}
          />

          {/* Bottom Gradient Overlay */}
          <div 
            className="absolute bottom-0 left-0 w-full z-10 pointer-events-none"
            style={{
              height: '45vh', // Cover bottom 45%
              background: `linear-gradient(to top, ${bgColor} 10%, transparent 100%)`
            }}
          />

          <div
            ref={innerRef}
            className="h-full overflow-y-auto scrollbar-hide text-center relative z-0" 
            style={{
              scrollBehavior: "auto",
              paddingLeft: `${horizontalPadding}%`,
              paddingRight: `${horizontalPadding}%`,
            }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
          <div ref={contentRef} style={{ willChange: "transform", position: "relative" }}>
            {/* Top padding to center first line */}
            <div style={{ height: centerOffset }} />
            
            {lines.map((line, index) => (
              <div
                key={index}
                data-index={index}
                style={{
                  fontSize: `${fontSize}px`,
                  lineHeight: lineHeight,
                  paddingTop: "0.1em",
                  paddingBottom: "0.1em",
                  color: fontColor,
                  // Opacity 1.0 because fading is handled by container mask
                  opacity: 1, 
                  fontWeight: 600,
                  transform: "translateZ(0)", // Hardware acceleration
                  transformOrigin: "center left",
                  willChange: "transform", // Hint to browser to promote to layer
                  whiteSpace: "pre-wrap", 
                  wordBreak: "keep-all",
                }}
              >
                {line || "\u00A0"}
              </div>
            ))}
            
            {/* Bottom padding to allow scrolling past last line */}
            <div style={{ height: containerHeight / 2 + centerOffset }} />
          </div>
        </div>
      </div>
    );
  }
), (prev, next) => {
  // Custom comparator:
  // If playing (both prev and next are playing), we IGNORE scrollPosition changes.
  // This prevents re-rendering ~10 times/sec just because scrollPosition prop changed,
  // since the component ignores prop-based scrolling while playing anyway.
  if (prev.isPlaying && next.isPlaying) {
      return (
          prev.content === next.content &&
          prev.fontSize === next.fontSize &&
          prev.fontColor === next.fontColor &&
          prev.bgColor === next.bgColor &&
          prev.mirrorMode === next.mirrorMode &&
          prev.lineHeight === next.lineHeight &&
          prev.horizontalPadding === next.horizontalPadding &&
          // Handlers are stable thanks to parent useCallback
          prev.onMouseMove === next.onMouseMove &&
          prev.onClick === next.onClick &&
          prev.onSwipeUp === next.onSwipeUp &&
          prev.onScrollUpdate === next.onScrollUpdate
      );
  }
  
  // Default shallow compare for when NOT playing (need to react to scrollPosition e.g. scrubber)
  return (
      prev.content === next.content &&
      prev.fontSize === next.fontSize &&
      prev.fontColor === next.fontColor &&
      prev.bgColor === next.bgColor &&
      prev.mirrorMode === next.mirrorMode &&
      prev.lineHeight === next.lineHeight &&
      prev.horizontalPadding === next.horizontalPadding &&
      prev.scrollPosition === next.scrollPosition &&
      prev.isPlaying === next.isPlaying &&
      prev.onMouseMove === next.onMouseMove &&
      prev.onClick === next.onClick &&
      prev.onSwipeUp === next.onSwipeUp &&
      prev.onScrollUpdate === next.onScrollUpdate
  );
});

TeleprompterDisplay.displayName = "TeleprompterDisplay";
